import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { fileUrl } from "@/lib/api";

const LOGIN_SIDE_FALLBACK = "https://images.unsplash.com/photo-1724582586413-6b69e1c94a17?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";

export default function AdminLogin() {
  const { login, verify2fa, user } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Honour ?next= so the PWA start_url can deep-link users to mobile capture
  // after login. Only accept in-app paths to prevent open-redirect abuse.
  const nextParam = params.get("next") || "";
  const nextPath = nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/admin";

  // If session is already active (e.g. re-opening the PWA), skip the form
  // and go straight to the requested destination.
  useEffect(() => {
    if (user) nav(nextPath, { replace: true });
  }, [user, nextPath, nav]);
  const [email, setEmail] = useState("admin@maisonpilet.com");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null); // {challenge_token, methods}
  const [method, setMethod] = useState("totp");
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const res = await login(email, password);
      if (res.requires_2fa) {
        setChallenge(res);
        setMethod(res.methods?.[0] || "totp");
        return;
      }
      nav(nextPath, { replace: true });
    } catch (e2) {
      setErr(typeof e2?.response?.data?.detail === "string" ? e2.response.data.detail : "Login failed");
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await verify2fa(challenge.challenge_token, method, code);
      nav(nextPath, { replace: true });
    } catch (e2) {
      setErr(typeof e2?.response?.data?.detail === "string" ? e2.response.data.detail : "verification failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-ivory" data-testid="admin-login-page">
      <div className="flex-1 hidden lg:block bg-linen relative">
        <img src={(settings?.login_side_path && fileUrl(settings.login_side_path)) || LOGIN_SIDE_FALLBACK} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="editorial-label text-ivory">maison pilet · studio access</p>
          <h2 className="font-serif text-5xl lowercase text-ivory mt-3 leading-tight drop-shadow-md">timeless over trendy.</h2>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-8 lg:px-20">
        {!challenge ? (
          <form onSubmit={submit} className="w-full max-w-md">
            <img src="/maison-pilet-logo.png" alt="Maison Pilet" className="h-16 w-auto mb-6" />
            <span className="editorial-label">studio sign-in</span>
            <h1 className="font-serif text-5xl lowercase text-charcoal mt-3 mb-12">welcome back.</h1>

            <label className="editorial-label">email</label>
            <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="w-full bg-transparent border-b border-taupe py-3 mt-2 mb-8 focus:outline-none focus:border-brass text-charcoal" />

            <label className="editorial-label">password</label>
            <input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="w-full bg-transparent border-b border-taupe py-3 mt-2 mb-10 focus:outline-none focus:border-brass text-charcoal" />

            {err && <p className="text-sm text-red-700 mb-6" data-testid="login-error">{err}</p>}

            <button data-testid="login-submit" disabled={loading} className="w-full bg-brass hover:bg-espresso text-ivory py-4 text-sm tracking-[0.25em] uppercase transition-colors disabled:opacity-50">
              {loading ? "signing in…" : "enter the studio"}
            </button>
            <div className="mt-6 flex items-center justify-between">
              <a href="/admin/forgot-password" data-testid="forgot-link" className="editorial-label hover:text-brass">forgot password?</a>
              <p className="editorial-label">curated with intention.</p>
            </div>
          </form>
        ) : (
          <form onSubmit={verify} className="w-full max-w-md" data-testid="login-2fa-step">
            <span className="editorial-label">studio · second factor</span>
            <h1 className="font-serif text-5xl lowercase text-charcoal mt-3 mb-3">one more step.</h1>
            <p className="text-sm text-espresso mb-8">enter the 6-digit code from your {method === "totp" ? "authenticator app" : "email"}.</p>

            {challenge.methods?.length > 1 && (
              <div className="flex gap-3 mb-8">
                {challenge.methods.map((m) => (
                  <button key={m} type="button" onClick={() => setMethod(m)} data-testid={`method-${m}`} className={`flex-1 px-3 py-2 text-[0.65rem] tracking-[0.25em] uppercase border ${method === m ? "border-brass text-brass" : "border-taupe text-taupe"}`}>
                    {m === "totp" ? "authenticator app" : "email code"}
                  </button>
                ))}
              </div>
            )}

            <label className="editorial-label">6-digit code</label>
            <input data-testid="login-2fa-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus className="w-full bg-transparent border-b border-taupe py-3 mt-2 mb-10 focus:outline-none focus:border-brass text-charcoal tracking-[0.5em] text-xl" />

            {err && <p className="text-sm text-red-700 mb-6" data-testid="login-2fa-error">{err}</p>}

            <button type="submit" data-testid="login-2fa-submit" disabled={loading} className="w-full bg-brass hover:bg-espresso text-ivory py-4 text-sm tracking-[0.25em] uppercase disabled:opacity-50">
              {loading ? "verifying…" : "verify & enter"}
            </button>
            <button type="button" onClick={() => { setChallenge(null); setCode(""); }} className="mt-4 editorial-label hover:text-brass w-full text-center">← back</button>
          </form>
        )}
      </div>
    </div>
  );
}
